import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, TrendingUp, Award, Users, BarChart3, ArrowLeft, FileSpreadsheet, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";

const GRADE_ORDER = ["Muito Bom", "Bom", "Suficiente", "Insuficiente", "Muito Insuficiente"];
const GRADE_HEX = {
  "Muito Bom": "#16a34a",
  "Bom": "#2563eb",
  "Suficiente": "#d97706",
  "Insuficiente": "#ea580c",
  "Muito Insuficiente": "#dc2626",
};

const gradeColor = (g) => {
  const map = {
    "Muito Bom": "bg-green-100 text-green-800 border-green-200",
    "Bom": "bg-blue-100 text-blue-800 border-blue-200",
    "Suficiente": "bg-amber-100 text-amber-800 border-amber-200",
    "Insuficiente": "bg-orange-100 text-orange-800 border-orange-200",
    "Muito Insuficiente": "bg-red-100 text-red-800 border-red-200",
  };
  return map[g] || "bg-gray-100";
};

export default function EvaluationMapPage() {
  const { id } = useParams();
  const [evaluation, setEvaluation] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const savedRef = useRef({});
  const savedCotacoes = useRef({});

  useEffect(() => { loadEvaluation(); }, [id]);

  const loadEvaluation = async () => {
    try {
      const res = await api.get(`/sessions/${id}/evaluation`);
      setEvaluation(res.data);
      savedRef.current = {};
      savedCotacoes.current = {};
      (res.data.questions || []).forEach((q) => {
        savedCotacoes.current[q.id] = q.max_score;
      });
      (res.data.students || []).forEach((s) => {
        savedRef.current[s.student_id] = {
          student_name: s.student_name || "",
          observations: s.observations || "",
        };
      });
      if (res.data.session?.logo_path) {
        const fileRes = await api.getFile(res.data.session.logo_path);
        setLogoUrl(URL.createObjectURL(fileRes.data));
      }
    } catch {
      toast.error("Erro ao carregar avaliação");
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.upload(`/sessions/${id}/upload-logo`, file);
      toast.success("Logo carregado");
      await loadEvaluation();
    } catch {
      toast.error("Erro ao carregar logo");
    } finally {
      setUploading(false);
    }
    e.target.value = "";
  };

  // ---- Inline student edits (name + observations) ----
  const setStudentField = (studentId, field, value) => {
    setEvaluation((prev) => ({
      ...prev,
      students: prev.students.map((s) =>
        s.student_id === studentId ? { ...s, [field]: value } : s
      ),
    }));
  };

  const saveStudentField = async (student, field) => {
    const value = (student[field] || "").trim();
    const prev = (savedRef.current[student.student_id]?.[field] || "").trim();
    if (value === prev) return;
    try {
      await api.put(`/sessions/${id}/students/${student.student_id}`, { [field]: value });
      savedRef.current[student.student_id] = {
        ...(savedRef.current[student.student_id] || {}),
        [field]: value,
      };
      toast.success(field === "student_name" ? "Nome atualizado" : "Observação guardada");
    } catch {
      toast.error("Erro ao guardar");
    }
  };

  // ---- Edit question cotação (max_score) + auto recalculate grades ----
  const setQuestionMax = (qid, value) => {
    setEvaluation((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === qid ? { ...q, max_score: value } : q)),
    }));
  };

  const saveCotacao = async (q) => {
    const val = parseFloat(q.max_score);
    if (isNaN(val) || val < 0) {
      toast.error("Cotação inválida");
      setQuestionMax(q.id, savedCotacoes.current[q.id]);
      return;
    }
    if (val === savedCotacoes.current[q.id]) return;
    try {
      await api.put(`/sessions/${id}/questions/${q.id}/cotacao`, { max_score: val });
      toast.success(`Cotação da Q${q.number} atualizada — notas recalculadas`);
      await loadEvaluation();
    } catch {
      toast.error("Erro ao atualizar cotação");
      setQuestionMax(q.id, savedCotacoes.current[q.id]);
    }
  };

  const downloadBlob = async (path, extension, mime, label, filename) => {
    const finalName = filename || `mapa_avaliacao_${evaluation?.session?.title?.replace(/\s+/g, "_") || "teste"}`;
    try {
      toast.info(`A gerar ${label}...`);
      const response = await api.get(path, { responseType: "blob" });
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.setAttribute("download", `${finalName}.${extension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${label} descarregado!`);
    } catch (err) {
      console.error("Export error:", err);
      try {
        const token = localStorage.getItem("edugrade_token");
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const url = `${backendUrl}/api${path}?auth=${token}`;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${finalName}.${extension}`);
        link.setAttribute("target", "_self");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch {
        toast.error(`Erro ao exportar ${label}.`);
      }
    }
  };

  const exportPDF = () =>
    downloadBlob(`/sessions/${id}/evaluation/pdf`, "pdf", "application/pdf", "PDF");
  const exportExcel = () =>
    downloadBlob(
      `/sessions/${id}/evaluation/excel`,
      "xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "Excel"
    );
  const downloadStudentReport = (s) => {
    const nm = (s.student_name || `aluno_${s.student_number}`).replace(/\s+/g, "_");
    downloadBlob(
      `/sessions/${id}/students/${s.student_id}/report/pdf`,
      "pdf",
      "application/pdf",
      "Ficha",
      `ficha_${s.student_number}_${nm}`
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!evaluation) return null;

  const { session, questions, students, statistics, question_statistics } = evaluation;

  const distribution = GRADE_ORDER.map((g) => ({
    grade: g,
    short: g === "Muito Insuficiente" ? "M. Insuf." : g === "Insuficiente" ? "Insuf." : g === "Suficiente" ? "Suf." : g,
    count: students.filter(
      (s) => s.grade === g && (s.status === "corrected" || s.status === "reviewed")
    ).length,
  }));
  const hasCorrected = distribution.some((d) => d.count > 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="evaluation-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to={`/tests/${id}/correction`}>
              <Button variant="ghost" size="sm" data-testid="back-correction-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
            </Link>
          </div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Mapa de Avaliacao</h1>
          <p className="text-muted-foreground mt-1 text-sm">{session.title}{session.subject ? ` · ${session.subject}` : ""}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" id="logo-upload" data-testid="logo-input" />
          <label htmlFor="logo-upload">
            <Button asChild variant="outline" disabled={uploading}>
              <span><Upload className="h-4 w-4 mr-2" />{uploading ? "..." : logoUrl ? "Alterar Logo" : "Adicionar Logo"}</span>
            </Button>
          </label>
          <Button onClick={exportExcel} variant="outline" className="active:scale-95 transition-transform" data-testid="export-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button onClick={exportPDF} className="active:scale-95 transition-transform" data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Logo + Title */}
      {logoUrl && (
        <div className="flex items-center gap-5 p-4 bg-white rounded-xl border">
          <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" data-testid="school-logo" />
          <Separator orientation="vertical" className="h-12" />
          <div>
            <p className="font-heading font-bold text-lg">{session.title}</p>
            {session.subject && <p className="text-muted-foreground text-sm">{session.subject}</p>}
          </div>
        </div>
      )}

      {/* Stats */}
      {statistics?.mean !== undefined && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-6 w-6 mx-auto text-primary mb-2" />
              <p className="font-mono text-2xl font-bold">{statistics.mean}</p>
              <p className="text-xs text-muted-foreground">Media</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center">
              <Award className="h-6 w-6 mx-auto text-amber-500 mb-2" />
              <p className="font-mono text-2xl font-bold">{statistics.max}</p>
              <p className="text-xs text-muted-foreground">Melhor Nota</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center">
              <Users className="h-6 w-6 mx-auto text-green-600 mb-2" />
              <p className="font-mono text-2xl font-bold">{statistics.pass_rate}%</p>
              <p className="text-xs text-muted-foreground">Aprovacao</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center">
              <BarChart3 className="h-6 w-6 mx-auto text-blue-600 mb-2" />
              <p className="font-mono text-2xl font-bold">{statistics.corrected_students}/{statistics.total_students}</p>
              <p className="text-xs text-muted-foreground">Corrigidos</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grade Distribution Chart */}
      {hasCorrected && (
        <Card data-testid="grade-distribution-chart">
          <CardHeader><CardTitle className="text-lg font-heading">Distribuicao das Classificacoes</CardTitle></CardHeader>
          <CardContent>
            <div className="w-full">
              <ResponsiveContainer width="100%" height={288} minWidth={0}>
                <BarChart data={distribution} margin={{ top: 20, right: 16, left: -10, bottom: 4 }}>
                  <XAxis dataKey="short" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    formatter={(value) => [`${value} aluno(s)`, "Alunos"]}
                    labelFormatter={(_, p) => p?.[0]?.payload?.grade || ""}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={90}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700 }} />
                    {distribution.map((d) => (
                      <Cell key={d.grade} fill={GRADE_HEX[d.grade]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grades Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Grelha de Classificacao</CardTitle>
          <p className="text-xs text-muted-foreground">Clique num nome, cotação (cabeçalho Q) ou observação para editar. As notas são recalculadas automaticamente.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 font-mono">N</TableHead>
                  <TableHead className="min-w-[160px]">Nome</TableHead>
                  {questions.map((q) => (
                    <TableHead key={q.id} className="text-center font-mono w-20 align-top">
                      <div className="pt-1">Q{q.number}</div>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={q.max_score}
                        onChange={(e) => setQuestionMax(q.id, e.target.value)}
                        onBlur={() => saveCotacao(q)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className="h-6 w-14 mx-auto mt-1 text-center text-xs px-1 font-normal"
                        title="Editar cotação (recalcula as notas)"
                        aria-label={`Cotação máxima da questão ${q.number}`}
                        data-testid={`cotacao-input-${q.number}`}
                      />
                    </TableHead>
                  ))}
                  <TableHead className="text-center font-mono">Total</TableHead>
                  <TableHead className="text-center font-mono">%</TableHead>
                  <TableHead className="text-center">Mencao</TableHead>
                  <TableHead className="min-w-[200px]">Obs. / Adaptacoes</TableHead>
                  <TableHead className="text-center w-20">Ficha Pais</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((s) => (
                  <TableRow key={s.student_id || s.student_number} data-testid={`eval-row-${s.student_number}`}>
                    <TableCell className="font-mono font-bold text-primary">{s.student_number}</TableCell>
                    <TableCell>
                      <Input
                        value={s.student_name || ""}
                        placeholder="Nome do aluno"
                        onChange={(e) => setStudentField(s.student_id, "student_name", e.target.value)}
                        onBlur={() => saveStudentField(s, "student_name")}
                        className="h-8 border-transparent hover:border-input focus:border-input px-2 font-medium"
                        data-testid={`name-input-${s.student_number}`}
                      />
                    </TableCell>
                    {questions.map((q) => (
                      <TableCell key={q.id} className="text-center font-mono text-sm">
                        {s.scores_by_question[q.number] != null ? s.scores_by_question[q.number].toFixed(1) : "-"}
                      </TableCell>
                    ))}
                    <TableCell className="text-center font-mono font-bold">{s.total_score != null ? s.total_score.toFixed(1) : "-"}</TableCell>
                    <TableCell className="text-center font-mono">{s.percentage}%</TableCell>
                    <TableCell className="text-center">
                      <Badge className={gradeColor(s.grade)}>{s.grade}</Badge>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={s.observations || ""}
                        placeholder="Ex: aluno NEE, teste adaptado..."
                        onChange={(e) => setStudentField(s.student_id, "observations", e.target.value)}
                        onBlur={() => saveStudentField(s, "observations")}
                        className="h-8 border-transparent hover:border-input focus:border-input px-2 text-sm"
                        data-testid={`obs-input-${s.student_number}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadStudentReport(s)}
                        title="Descarregar ficha de resultado para os pais (PDF)"
                        data-testid={`report-btn-${s.student_number}`}
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Question Stats */}
      {question_statistics?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">Analise por Questao</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono">Q</TableHead>
                  <TableHead>Enunciado</TableHead>
                  <TableHead className="text-center font-mono">Cotacao</TableHead>
                  <TableHead className="text-center font-mono">Media</TableHead>
                  <TableHead className="text-center">Sucesso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {question_statistics.map((qs) => (
                  <TableRow key={qs.number}>
                    <TableCell className="font-mono font-bold text-primary">Q{qs.number}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate">{qs.text}</TableCell>
                    <TableCell className="text-center font-mono">{qs.max_score}</TableCell>
                    <TableCell className="text-center font-mono">{qs.mean}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={qs.success_rate >= 50 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                        {qs.success_rate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
